/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

export type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
}

// Exported so a real <Link> (e.g. EmptyState's cta.to) can look identical to
// this button without duplicating the class strings or losing <button>'s
// semantics by rendering an anchor through this component.
export const buttonVariantClasses: Record<Variant, string> = {
  primary:
    'bg-primary hover:bg-primary-700 active:bg-primary-800 text-white shadow-sm hover:shadow-md',
  secondary: 'bg-white border border-border hover:bg-slate-50 text-text-primary',
  ghost: 'hover:bg-slate-50 text-text-secondary hover:text-text-primary',
  danger: 'bg-destructive/10 hover:bg-destructive/20 text-destructive',
};

export const buttonSizeClasses: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  md: 'px-4 py-2 text-sm rounded-xl gap-2',
  lg: 'px-5 py-2.5 text-sm rounded-xl gap-2',
};

export const buttonBaseClasses =
  'inline-flex items-center justify-center font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

const variants = buttonVariantClasses;
const sizes = buttonSizeClasses;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...props },
    ref
  ) => (
    <motion.button
      ref={ref}
      whileTap={{ scale: 0.97 }}
      className={clsx(buttonBaseClasses, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      {...(props as any)}
    >
      {loading ? <Loader2 size={size === 'sm' ? 14 : 16} className="animate-spin" /> : icon}
      {children}
    </motion.button>
  )
);
Button.displayName = 'Button';
export default Button;
